import { useState } from 'react';
import { Check, Lock, Palette } from 'lucide-react';
import { supabase } from '@/core/supabase';
import { useSession } from '@/core/session';
import { useTheme, THEME_PRESETS, type ThemePreset } from '@/core/theme';
import { Card, CardHeader, Button, Pill, cn } from '@/components/ui';
import { BiometricCard } from '@/components/BiometricCard';
import { AvatarPicker } from '@/components/AvatarPicker';
import { ADMIN_NAV } from '@/app/AppShell';
import { useQuickNav, MAX_QUICK } from '@/app/useQuickNav';
import { useT } from '@/core/i18n';

/**
 * Two settings that look similar and are not.
 *
 * The preset is the company's visual identity — one choice for everyone, and
 * only an admin may change it. Light or dark is personal, and every user picks
 * their own from the header at any time.
 */
export function AppearanceSettings() {
  const t = useT();
  const { company, can, refresh } = useSession();
  const { setPreset, companyPreset, personalPreset, setPersonalPreset } = useTheme();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mayChange = can('settings.manage');

  async function choosePreset(next: ThemePreset) {
    if (!mayChange || !company) return;

    /* Apply immediately so the choice is visible while it saves — and restore
       the *company's* previous value on failure, not the resolved one. Reading
       `preset` here would write somebody's personal override back as the
       company theme the first time a save failed. */
    const previous = companyPreset;
    setPreset(next);
    setSaving(true);
    setError(null);
    setSaved(false);

    const { error: updateError } = await supabase
      .from('companies')
      .update({ theme_preset: next })
      .eq('id', company.id);

    setSaving(false);

    if (updateError) {
      setPreset(previous);
      setError(t('app.themeFailed'));
      return;
    }

    setSaved(true);
    void refresh();
    window.setTimeout(() => setSaved(false), 2500);
  }

  return (
    <div className="max-w-3xl flex flex-col gap-5">
      <div>
        <h1 className="text-title font-semibold tracking-tight">{t('set.appearance')}</h1>
        <p className="text-[13px] text-muted mt-0.5">
          {t('ap.companyTheme')}
        </p>
      </div>

      {/* First, because it is the one thing on this page that is unarguably
          theirs. The theme below is the company's and they may not be allowed
          to touch it at all. */}
      <AvatarPicker />

      <Card>
        <CardHeader
          title={t('app.companyTheme')}
          subtitle={t('app.companyThemeSub')}
          action={
            mayChange ? (
              saved ? <Pill tone="ok"><Check size={11} /> {t('common.saved')}</Pill> : null
            ) : (
              <Pill tone="neutral"><Lock size={11} /> {t('co.adminOnly')}</Pill>
            )
          }
        />

        {!mayChange && (
          <p className="text-[12.5px] text-muted bg-surface-2 rounded-card px-3 py-2.5 mb-4 leading-snug">
            {t('ap.adminOnly')}
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-3">
          {THEME_PRESETS.map((option) => {
            /* The company's, not what is on screen. Somebody with a personal
               override open on this page would otherwise be shown their own
               choice ticked as the company's, and could change the company's
               theme believing they were confirming it. */
            const active = companyPreset === option.id;
            return (
              <button
                key={option.id}
                type="button"
                disabled={!mayChange || saving}
                onClick={() => void choosePreset(option.id)}
                aria-pressed={active}
                className={cn(
                  'text-start p-3.5 rounded-card border-2 transition-all',
                  active ? 'border-brand bg-brand-subtle' : 'border-line bg-surface',
                  mayChange && !active && 'hover:border-line-strong',
                  (!mayChange || saving) && 'opacity-60 cursor-not-allowed',
                )}
              >
                <div className="flex items-center justify-between mb-2.5">
                  <div className="flex gap-1">
                    {/* Cornered with the preset's own radius rather than a fixed
                        one. These nine differ in geometry as well as colour —
                        Graphite is near-square, Orchid is generously round — and
                        a swatch that hid that would preview half the choice. */}
                    {option.swatch.map((hex) => (
                      <span
                        key={hex}
                        className="w-6 h-6 border border-black/10"
                        style={{ background: hex, borderRadius: option.radius }}
                      />
                    ))}
                  </div>
                  {active && <Check size={15} className="text-brand" />}
                </div>
                <p className="text-[13px] font-semibold">{option.name}</p>
                <p className="text-[11.5px] text-muted mt-0.5">{option.description}</p>
              </button>
            );
          })}
        </div>

        {error && (
          <p role="alert" className="text-[12.5px] text-danger bg-danger-subtle rounded-card px-3 py-2.5 mt-4">
            {error}
          </p>
        )}
      </Card>

      <Card>
        <CardHeader
          title={t('app.yourColours')}
          subtitle={t('app.yourColoursSub')}
        />
        <p className="text-[12.5px] text-muted leading-relaxed mb-4">
          {/* The reason, because a preference with no reason attached reads as
              decoration and gets removed by the next person tidying the screen.
              Somebody who cannot read one palette should not have to ask an
              administrator to change the whole company's colours. */}
          {t('ap.pickAnyOfNine')}
        </p>

        <div className="grid gap-2 sm:grid-cols-3">
          {THEME_PRESETS.map((option) => {
            const active = personalPreset === option.id;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => setPersonalPreset(active ? null : option.id)}
                aria-pressed={active}
                className={cn(
                  'flex items-center gap-2 text-start p-2.5 rounded-card border transition-all',
                  active ? 'border-brand bg-brand-subtle' : 'border-line bg-surface hover:border-line-strong',
                )}
              >
                <span className="flex gap-1 shrink-0">
                  {option.swatch.slice(0, 2).map((hex) => (
                    <span
                      key={hex}
                      className="w-4 h-4 border border-black/10"
                      style={{ background: hex, borderRadius: option.radius }}
                    />
                  ))}
                </span>
                <span className="text-[12.5px] font-medium truncate">{option.name}</span>
                {active && <Check size={14} className="text-brand ms-auto shrink-0" />}
              </button>
            );
          })}
        </div>

        {personalPreset && (
          <button
            type="button"
            onClick={() => setPersonalPreset(null)}
            className="text-[12.5px] text-brand hover:underline mt-3"
          >
            {t('ap.useCompanyAgain')}
            {' '}
            <span className="text-muted">
              ({THEME_PRESETS.find((p) => p.id === companyPreset)?.name})
            </span>
          </button>
        )}
      </Card>

      <QuickNavPicker />

      <Card>
        <CardHeader
          title={t('app.lightAndDark')}
          subtitle={t('app.lightAndDarkSub')}
        />
        <p className="text-[13px] text-muted leading-relaxed">
          {t('ap.headerControl')}
        </p>
        <div className="mt-4">
          <Button
            variant="secondary"
            size="sm"
            icon={<Palette size={14} />}
            onClick={() =>
              document
                .querySelector<HTMLButtonElement>('[data-scheme-toggle]')
                ?.click()
            }
          >
            {t('app.openMenu')}
          </Button>
        </div>
      </Card>

      {/* Renders nothing in a browser or on a handset with no reader. */}
      <BiometricCard />
    </div>
  );
}

/* -----------------------------------------------------------------------------
 * The quick bar
 * -----------------------------------------------------------------------------
 * Only on a phone, which is the only place the bar appears — and said so, rather
 * than leaving somebody on a desktop to choose five modules and watch nothing
 * happen.
 * -------------------------------------------------------------------------- */
function QuickNavPicker() {
  const t = useT();
  const { can } = useSession();
  const available = ADMIN_NAV.filter((item) => !item.permission || can(item.permission));
  const { chosen, routes, save, clear, canSave } = useQuickNav(available);

  const [draft, setDraft] = useState<string[]>(() => chosen.map((c) => c.to));

  function toggle(to: string) {
    setDraft((current) => (
      current.includes(to)
        ? current.filter((t) => t !== to)
        // Appended rather than inserted, so the bar reads in the order somebody
        // picked rather than the order this list happens to be written in.
        : current.length >= MAX_QUICK ? current : [...current, to]
    ));
  }

  return (
    <Card>
      <CardHeader
        title={t('app.quickBar')}
        subtitle={t('ap.upToModules', { max: MAX_QUICK })}
      />

      <p className="text-[12.5px] text-muted leading-relaxed mb-4">
        {/* No recommended five, and saying why. */}
        {t('ap.noRightSet')}
        {routes === null && ' Until you do, the bar shows the first few you can open.'}
      </p>

      <div className="grid gap-2 sm:grid-cols-2">
        {available.map(({ to, labelKey, icon: Icon }) => {
          const on = draft.includes(to);
          const full = !on && draft.length >= MAX_QUICK;

          return (
            <button
              key={to}
              type="button"
              disabled={full}
              onClick={() => toggle(to)}
              aria-pressed={on}
              className={cn(
                'flex items-center gap-2.5 text-start p-2.5 rounded-card border transition-all',
                on ? 'border-brand bg-brand-subtle' : 'border-line bg-surface',
                full && 'opacity-40 cursor-not-allowed',
              )}
            >
              <Icon size={16} className="shrink-0" />
              <span className="text-[12.5px] font-medium truncate">{t(labelKey)}</span>
              {on && (
                <span className="ms-auto text-[11px] font-mono text-brand shrink-0">
                  {draft.indexOf(to) + 1}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2 mt-4">
        <Button onClick={() => save(draft)} disabled={!canSave(draft)}>
          {t('ap.saveTheBar')}
        </Button>
        {routes !== null && (
          <Button
            variant="ghost"
            onClick={() => { clear(); setDraft(available.slice(0, 5).map((a) => a.to)); }}
          >
            {t('ap.backToDefault')}
          </Button>
        )}
      </div>

      {!canSave(draft) && (
        /* The rule, not just a dead button. Two is where a bar starts being
           quicker than the menu it replaces. */
        <p className="text-[11.5px] text-subtle mt-2">
          Pick at least two and at most {MAX_QUICK}.
        </p>
      )}
    </Card>
  );
}
